import equal from 'fast-deep-equal';
import { ChildQuestionTemplate, FormQuestionItem, Question } from '../types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SlotAction =
  'keep' | 'update-text' | 'update-key' | 'update-meta' | 'recreate' | 'create' | 'remove';

export type ChildSlotChange = {
  actions: SlotAction[];
  templateId: string;
  optionGuid: string;
  existingChild?: FormQuestionItem;
  newText?: string;
  newKey?: string;
  metaUpdates?: Partial<FormQuestionItem>;
};

// ---------------------------------------------------------------------------
// computeNewKey
// ---------------------------------------------------------------------------

/**
 * Returns the new default key for a child question after its parent key or
 * template key_prefix changed.  Returns `undefined` when the child's current
 * key was customised (doesn't match the old default pattern) or when the new
 * candidate key would collide with another question key.
 */
export function computeNewKey(
  child: FormQuestionItem,
  origParentKey: string | undefined,
  newParentKey: string | undefined,
  origTemplate: ChildQuestionTemplate | undefined,
  newTemplate: ChildQuestionTemplate,
  allQuestionKeys: Set<string>,
  optionIndex: number,
  optionGuid: string,
): string | undefined {
  const currentKey = child.question_key;
  if (!currentKey) return undefined;

  // Compute what the OLD default key would have been (exact string, no regex needed).
  // This is more precise than a regex and avoids dynamic RegExp construction.
  const oldShortTemplateId = (origTemplate?.id ?? newTemplate.id).substring(0, 6);
  const shortOptionGuid = optionGuid.substring(0, 6);
  const oldPrefix = origTemplate?.key_prefix?.trim();

  const oldDefaultKey = oldPrefix
    ? origParentKey
      ? `${origParentKey}_${oldPrefix}_${optionIndex + 1}`
      : `${oldPrefix}_${optionIndex + 1}`
    : origParentKey
      ? `${origParentKey}_child_${oldShortTemplateId}_${optionIndex + 1}_${shortOptionGuid}`
      : `child_${oldShortTemplateId}_${optionIndex + 1}_${shortOptionGuid}`;

  if (currentKey.toLowerCase() !== oldDefaultKey.toLowerCase()) return undefined; // customised key

  // Compute the NEW default key using the same logic as createChildQuestions
  const shortTemplateId = newTemplate.id.substring(0, 6);
  const newPrefix = newTemplate.key_prefix?.trim();

  let candidateKey: string;
  if (newPrefix) {
    candidateKey = newParentKey
      ? `${newParentKey}_${newPrefix}_${optionIndex + 1}`
      : `${newPrefix}_${optionIndex + 1}`;
  } else {
    candidateKey = newParentKey
      ? `${newParentKey}_child_${shortTemplateId}_${optionIndex + 1}_${shortOptionGuid}`
      : `child_${shortTemplateId}_${optionIndex + 1}_${shortOptionGuid}`;
  }

  // Don't apply if it would collide (excluding the child's own current key)
  const keysWithoutSelf = new Set(allQuestionKeys);
  keysWithoutSelf.delete(currentKey.toLowerCase());
  if (keysWithoutSelf.has(candidateKey.toLowerCase())) return undefined;

  return candidateKey;
}

// ---------------------------------------------------------------------------
// computeChildChanges
// ---------------------------------------------------------------------------

/**
 * Compares the original parent question to the updated one and produces a
 * ChildSlotChange entry for every (templateId × optionGuid) slot that exists
 * in either the old or new configuration.
 *
 * Rules:
 *  - No existing child for a slot  → `create`
 *  - Structural change (question_type / options / field_type) → `recreate`
 *  - text_before / text_after / option text changed → `update-text`
 *  - key_prefix / parent question_key changed → `update-key`
 *  - badge_color / measurement_unit / description / show_description changed → `update-meta`
 *  - Slot no longer in new templates → `remove`
 *  - Nothing changed → `keep`
 */
export function computeChildChanges(
  origParent: Question,
  newParent: Question,
  existingChildren: FormQuestionItem[],
): ChildSlotChange[] {
  const changes: ChildSlotChange[] = [];
  const newTemplates = newParent.child_question_templates ?? [];

  // Build a lookup: templateId_optionGuid -> existing child
  const childBySlot = new Map<string, FormQuestionItem>();
  for (const child of existingChildren) {
    const cond = child.appeared_conditions?.find(
      (c) => c.connected_question_guid === origParent.guid,
    );
    const optGuid = cond?.values?.[0];
    if (optGuid && child.auto_generated_from_template) {
      childBySlot.set(`${child.auto_generated_from_template}_${optGuid}`, child);
    }
  }

  // Track slots still present in the new config
  const processedSlots = new Set<string>();

  for (const newTemplate of newTemplates) {
    const origTemplate = origParent.child_question_templates?.find((t) => t.id === newTemplate.id);

    for (let optIdx = 0; optIdx < (newTemplate.option_guids?.length ?? 0); optIdx++) {
      const optGuid = newTemplate.option_guids[optIdx];
      const slotKey = `${newTemplate.id}_${optGuid}`;
      processedSlots.add(slotKey);

      const existingChild = childBySlot.get(slotKey);
      const actions: SlotAction[] = [];
      const change: ChildSlotChange = {
        actions,
        templateId: newTemplate.id,
        optionGuid: optGuid,
        existingChild,
      };

      if (!existingChild) {
        actions.push('create');
        changes.push(change);
        continue;
      }

      // Check for structural changes → recreate
      const structureChanged =
        origTemplate?.question_type !== newTemplate.question_type ||
        origTemplate?.field_type !== newTemplate.field_type ||
        !equal(origTemplate?.options ?? [], newTemplate.options ?? []);

      if (structureChanged) {
        actions.push('recreate');
        changes.push(change);
        continue;
      }

      // Check for text change
      const newOption = newParent.options?.find((o) => o.guid === optGuid);
      const origOption = origParent.options?.find((o) => o.guid === optGuid);
      const origText = `${origTemplate?.text_before ?? ''}${String(origOption?.text ?? '')}${origTemplate?.text_after ?? ''}`;
      const newText = `${newTemplate.text_before ?? ''}${String(newOption?.text ?? '')}${newTemplate.text_after ?? ''}`;
      if (origText !== newText) {
        actions.push('update-text');
        change.newText = newText;
      }

      // Check for key change
      const keyPrefixChanged = (origTemplate?.key_prefix ?? '') !== (newTemplate.key_prefix ?? '');
      const parentKeyChanged = origParent.question_key !== newParent.question_key;
      if (keyPrefixChanged || parentKeyChanged) {
        const allKeys = new Set(
          existingChildren.map((c) => c.question_key?.toLowerCase()).filter(Boolean) as string[],
        );
        const newKey = computeNewKey(
          existingChild,
          origParent.question_key,
          newParent.question_key,
          origTemplate,
          newTemplate,
          allKeys,
          optIdx,
          optGuid,
        );
        if (newKey) {
          actions.push('update-key');
          change.newKey = newKey;
        }
      }

      // Check for meta changes
      const metaUpdates: Partial<FormQuestionItem> = {};
      if ((origTemplate?.badge_color ?? '') !== (newTemplate.badge_color ?? ''))
        metaUpdates.auto_generated_badge_color = newTemplate.badge_color;
      if ((origTemplate?.measurement_unit ?? '') !== (newTemplate.measurement_unit ?? ''))
        metaUpdates.measurement_unit = newTemplate.measurement_unit;
      if ((origTemplate?.description ?? '') !== (newTemplate.description ?? ''))
        metaUpdates.description = newTemplate.description;
      if ((origTemplate?.show_description ?? false) !== (newTemplate.show_description ?? false))
        metaUpdates.show_description = newTemplate.show_description ?? false;

      if (Object.keys(metaUpdates).length > 0) {
        actions.push('update-meta');
        change.metaUpdates = metaUpdates;
      }

      if (actions.length === 0) actions.push('keep');
      changes.push(change);
    }
  }

  // Any existing child whose slot is no longer in the new config → remove
  Array.from(childBySlot.entries()).forEach(([slotKey, child]) => {
    if (!processedSlots.has(slotKey)) {
      const [templateId, optionGuid] = slotKey.split(/_(.+)/); // split on first _ only
      changes.push({
        actions: ['remove'],
        templateId,
        optionGuid,
        existingChild: child,
      });
    }
  });

  return changes;
}
